// Comet Dive (owner 2026-11-02: 'Aren jumps high and crashes down… leaving a deep crater and a shockwave that launches foes
// into the air'). Render-only: reads sim.dive and the diveStart / diveLand / diveShock / diveFissure events. The sim moves the
// hero along the ground path; this file supplies the height of the leap (view.ts lifts the hero by `lift`) and the effects:
//  - take-off: a dust ring kicked out from his feet, a small dent, a white streak upward
//  - the crash-down: a comet shell (white-hot head round the hero, orange body, ragged red flame tail streaming back up the
//    path) plus a fire ribbon trail and embers
//  - landing: white flash, crisp shock rings, fireball + rocks + dust curtain (blastFx), molten cracks radiating from the
//    crater (the crater itself comes from the sim's 'crater' event), ground light, shake + a hair of hitstop
//  - aftershock: a second wider ring of dust and light; Titan Drop fissures: glowing jagged rifts that erupt in fire and rock
// Pooled; no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, color, float, length, max, min, mix, mx_noise_float, step, uniform, uv, vec2, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import { DIVE_H } from '../core/skillStats';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundDeform } from './ground';
import type { GroundGlow } from './groundGlow';
import { RibbonStrips, RS_STRIDE } from './ribbonStrips';

const HOT = 0xfff1c8, ORANGE = 0xff8a3a, EMBER = 0xff5a1a, DUST = 0xb89a78;
/** share of the airtime spent rising (the rest is the steep crash) */
const RISE = 0.42;
const CRACKS = 18, CRACK_PTS = 9, CRACK_LIFE = 1.6;
const TRAIL = 14;

type Ev<T extends SimEvent['t']> = Extract<SimEvent, { t: T }>;
interface Crack { on: boolean; t: number; life: number; w: number; n: number; p: Float32Array; hot: boolean }

const _x = new THREE.Vector3(), _y = new THREE.Vector3(), _z = new THREE.Vector3(), _v = new THREE.Vector3(), _a = new THREE.Vector3(), _b = new THREE.Vector3(), _m = new THREE.Matrix4();
const _edge = new THREE.Color(), _core = new THREE.Color();

/** Roaring Meteor (owner 2026-11-03 rework): the leap rises for the first METEOR_RISE of the airtime, hangs at the apex until
 *  METEOR_FALL, then crashes steeply; METEOR_H × DIVE_H high (the sim's ground path is the same as Comet Dive's). */
export const METEOR_RISE = 0.40, METEOR_FALL = 0.54, METEOR_H = 1.75;
/** height of the leap at airtime share u (0..1): a quick rise, a brief hang, then a steep crash (meteor: higher, longer hang) */
export function diveHeight(u: number, h = DIVE_H, meteor = false): number {
  if (u <= 0 || u >= 1) return 0;
  if (meteor) {
    if (u < METEOR_RISE) return h * (1 - (1 - u / METEOR_RISE) ** 2.4);
    if (u < METEOR_FALL) return h * (1 + 0.025 * Math.sin((u - METEOR_RISE) / (METEOR_FALL - METEOR_RISE) * Math.PI));
    return h * (1 - ((u - METEOR_FALL) / (1 - METEOR_FALL)) ** 2.6);
  }
  return u < RISE ? h * (1 - (1 - u / RISE) ** 2) : h * (1 - ((u - RISE) / (1 - RISE)) ** 2.4);
}

export class CometDiveFx {
  /** hero lift above the ground (m), forward pitch while crashing (rad), facing yaw; airborne = a dive is in progress */
  lift = 0; pitch = 0; yaw = 0; airborne = false;
  /** Roaring Meteor leap (sim.dive.fx === 'roarfall'): higher, with a hang at the apex; the landing FX belong to roarFall.ts */
  meteor = false;
  /** QA counters */
  lands = 0;
  /** QA: hold the leap at this airtime share (0..1) for captures; null = follow the sim */
  hold: number | null = null;
  private tl = 0; private dur = 0.55; private x0 = 0; private z0 = 0; private x1 = 0; private z1 = 0; private evo = false;
  private comet: THREE.Mesh; private cometT = uniform(0); private cometK = uniform(0);
  private trail = new Float32Array(TRAIL * 3); private trailN = 0; private trailAcc = 0;
  private ribbons: RibbonStrips;
  private cracks: Crack[] = [];
  private prev = new THREE.Vector3(); private hasPrev = false;

  constructor(scene: THREE.Scene, private ground: GroundDeform) {
    this.ribbons = new RibbonStrips(scene, CRACKS + 4, 16, false, 2);
    for (let i = 0; i < CRACKS; i++) this.cracks.push({ on: false, t: 9, life: 1, w: 0.2, n: 0, p: new Float32Array(CRACK_PTS * 3), hot: false });
    // comet shell: card along the motion; head ring round the hero (hollow so he stays visible), flame tail behind
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const A = 3.2, t = this.cometT;                                      // card length / height in head units
    const X = uv().x.mul(A), Y = uv().y.sub(0.5), cx = float(A - 0.55);
    const along = X.div(cx).clamp(0, 1);
    const n1 = mx_noise_float(vec3(X.mul(2.6).add(t.mul(13)), Y.mul(4.5), 1.3)), n2 = mx_noise_float(vec3(X.mul(6).add(t.mul(22)), Y.mul(8), 4.1));
    const flick = n1.mul(0.7).add(n2.mul(0.3)), rag = float(0.05).add(float(1).sub(along).mul(0.16));
    const dHead = length(vec2(X.sub(cx), Y)).sub(0.5);
    const dTail = max(abs(Y).sub(along.mul(0.46)), X.sub(cx));
    const dOut = min(dHead, dTail).add(flick.mul(rag));
    const inside = step(dOut, 0);
    const rim = step(float(-0.07), dOut);
    const hollow = step(0.36, length(vec2(X.sub(cx), Y)));                // the hero shows through the head
    const hot = step(dOut, float(-0.16)).mul(step(0.5, along));            // white-hot inner band along the front
    let col = mix(color(ORANGE), color(HOT), hot);
    col = mix(col, color(0xd8321a), rim);
    mat.colorNode = col.mul(1.5);
    mat.opacityNode = inside.mul(mix(float(1), hollow, step(0.86, along))).mul(float(0.6).add(along.mul(0.4))).mul(this.cometK);
    this.comet = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat); this.comet.visible = false; this.comet.frustumCulled = false; this.comet.renderOrder = 4;
    scene.add(this.comet);
  }

  private crack(pts: number[], w: number, life: number, hot: boolean): void {
    const c = this.cracks.reduce((a, b) => (!b.on ? (a.on ? b : a) : !a.on ? a : b.t > a.t ? b : a));
    c.on = true; c.t = 0; c.life = life; c.w = w; c.hot = hot; c.n = Math.min(CRACK_PTS, pts.length / 3);
    for (let i = 0; i < c.n * 3; i++) c.p[i] = pts[i];
  }
  /** a jagged crack from (x, z) along angle a, from r0 to r1 metres out, hugging the ground */
  private radial(x: number, z: number, a: number, r0: number, r1: number, w: number, life: number, hot: boolean): void {
    const pts: number[] = []; let aa = a;
    for (let i = 0; i < CRACK_PTS; i++) {
      const u = i / (CRACK_PTS - 1), r = r0 + (r1 - r0) * u; aa += (Math.random() - 0.5) * 0.35;
      const px = x + Math.cos(aa) * r, pz = z + Math.sin(aa) * r;
      pts.push(px, this.ground.sample(px, pz) + 0.07, pz);
    }
    this.crack(pts, w, life, hot);
  }

  event(ev: SimEvent, fx: Fx, glow: GroundGlow): void {
    switch (ev.t) {
      case 'diveStart': this.start(ev, fx); break;
      case 'diveLand': this.land(ev, fx, glow); break;
      case 'diveShock': {
        const gy = this.ground.sample(ev.x, ev.z);
        fx.rings.add(ev.x, gy + 0.15, ev.z, ev.r, 0.45, 0xffb070, 2.4, 1.2);
        fx.rings.add(ev.x, gy + 0.1, ev.z, ev.r * 1.1, 0.7, DUST, 1.2, 1.8);
        for (let k = 0; k < 18; k++) { const a = (k / 18) * 6.283 + Math.random() * 0.3, R = ev.r * 0.55; fx.blast?.dust(ev.x + Math.cos(a) * R, gy + 0.3, ev.z + Math.sin(a) * R, Math.cos(a) * ev.r * 2.2, 0.6, Math.sin(a) * ev.r * 2.2, 0.8 + Math.random() * 0.4, 0.5, 1.6 + Math.random()); }
        for (let k = 0; k < 7; k++) this.radial(ev.x, ev.z, (k / 7) * 6.283 + Math.random() * 0.5, ev.r * 0.45, ev.r * (0.8 + Math.random() * 0.25), 0.16, 0.9, false);
        glow.flash(ev.x, ev.z, ev.r * 1.1, ORANGE, 0.6, 0.35);
        this.ground.stampDent(ev.x, ev.z, ev.r * 0.9, 0.12);
        fx.shake = Math.min(1, fx.shake + 0.3);
        break;
      }
      case 'diveFissure': {
        const x0 = ev.x + ev.dx * ev.from, z0 = ev.z + ev.dz * ev.from, L = ev.to - ev.from, a = Math.atan2(ev.dz, ev.dx);
        this.radial(ev.x, ev.z, a, ev.from, ev.to, 0.42, 1.8, true);
        this.ground.stampTrench(x0, z0, ev.dx, ev.dz, L, 0.45);
        for (let s = 0.4; s < L; s += 1.1) {
          const px = x0 + ev.dx * s, pz = z0 + ev.dz * s, gy = this.ground.sample(px, pz);
          fx.blast?.pop(px, gy + 0.4, pz, ORANGE);
          fx.blast?.chips(px, pz, -ev.dz, ev.dx, 2); fx.blast?.chips(px, pz, ev.dz, -ev.dx, 2);
          fx.particles.emitDir(px, gy + 0.2, pz, ev.dx * 2, 5 + Math.random() * 4, ev.dz * 2, Math.random() < 0.5 ? HOT : EMBER, 0.25, 0.5, 2.6);
        }
        glow.flash(x0 + ev.dx * L / 2, z0 + ev.dz * L / 2, L * 0.6, EMBER, 0.7, 0.6);
        fx.shake = Math.min(1, fx.shake + 0.08);
        break;
      }
    }
  }

  private start(ev: Ev<'diveStart'>, fx: Fx): void {
    this.airborne = true; this.tl = 0; this.dur = ev.dur; this.x0 = ev.x0; this.z0 = ev.z0; this.x1 = ev.x1; this.z1 = ev.z1; this.evo = ev.evolved;
    this.trailN = 0; this.hasPrev = false;
    const dx = ev.x1 - ev.x0, dz = ev.z1 - ev.z0; this.yaw = Math.hypot(dx, dz) > 0.2 ? Math.atan2(dx, dz) : this.yaw;
    const gy = this.ground.sample(ev.x0, ev.z0);
    // take-off: dust kicked out from his feet, a little dent, pebbles, a streak of light upward
    for (let k = 0; k < 14; k++) { const a = (k / 14) * 6.283 + Math.random() * 0.4; fx.blast?.dust(ev.x0 + Math.cos(a) * 0.5, gy + 0.2, ev.z0 + Math.sin(a) * 0.5, Math.cos(a) * 5, 0.5, Math.sin(a) * 5, 0.6 + Math.random() * 0.3, 0.4, 1.3); }
    fx.rings.add(ev.x0, gy + 0.12, ev.z0, 2.2, 0.3, 0xffd0a0, 2, 1.4);
    for (let k = 0; k < 10; k++) fx.particles.emitDir(ev.x0 + (Math.random() - 0.5) * 0.6, gy + 0.3, ev.z0 + (Math.random() - 0.5) * 0.6, (Math.random() - 0.5) * 2, 9 + Math.random() * 6, (Math.random() - 0.5) * 2, k % 2 ? 0xffffff : HOT, 0.16, 0.3, 2.6);
    this.ground.stampDent(ev.x0, ev.z0, 1.1, 0.1);
    fx.shake = Math.min(1, fx.shake + 0.12);
  }

  /** a fusion leap landed and another module draws the landing: just come down (no FX) */
  settle(): void { this.lands++; this.airborne = false; this.lift = 0; this.pitch = 0; this.comet.visible = false; this.meteor = false; }

  private land(ev: Ev<'diveLand'>, fx: Fx, glow: GroundGlow): void {
    this.lands++; this.airborne = false; this.lift = 0; this.pitch = 0; this.comet.visible = false;
    const gy = this.ground.sample(ev.x, ev.z), r = ev.r, big = ev.evolved;
    fx.flashes.add(ev.x, gy + 0.6, ev.z, r * 0.55, 0.16, HOT, 1.3);
    fx.stars.add(ev.x, gy + 0.9, ev.z, r * 1.1, 0.16, 0xffffff, 2.8);
    fx.stars.add(ev.x, gy + 0.9, ev.z, r * 1.9, 0.26, ORANGE, 1.6);
    // crisp shock rings racing out along the ground, then a slower dust ring
    fx.rings.add(ev.x, gy + 0.15, ev.z, r * 1.15, 0.3, 0xffd6a0, 3.2, 1.6);
    fx.rings.add(ev.x, gy + 0.25, ev.z, r * 0.8, 0.22, 0xffffff, 3, 1);
    fx.rings.add(ev.x, gy + 0.1, ev.z, r * 1.7, 0.8, DUST, 1.3, 2.2);
    fx.blast?.blast(ev.x, gy + 0.6, ev.z, r * (big ? 0.5 : 0.48), big, ORANGE);
    // a dust curtain rolling out from the rim, and rocks thrown out of the crater
    for (let k = 0; k < 22; k++) { const a = (k / 22) * 6.283 + Math.random() * 0.25, R = ev.cr * 0.8; fx.blast?.dust(ev.x + Math.cos(a) * R, gy + 0.3, ev.z + Math.sin(a) * R, Math.cos(a) * r * 2.6, 0.8 + Math.random(), Math.sin(a) * r * 2.6, 0.9 + Math.random() * 0.5, 0.6, 2 + Math.random() * 1.2); }
    for (let k = 0; k < 8; k++) { const a = Math.random() * 6.283; fx.blast?.chips(ev.x + Math.cos(a) * ev.cr * 0.5, ev.z + Math.sin(a) * ev.cr * 0.5, Math.cos(a), Math.sin(a), 2); }
    // molten cracks radiating from the crater
    const n = big ? 11 : 8;
    for (let k = 0; k < n; k++) this.radial(ev.x, ev.z, (k / n) * 6.283 + Math.random() * 0.4, ev.cr * 0.35, r * (0.85 + Math.random() * 0.35), big ? 0.5 : 0.38, CRACK_LIFE, true);
    this.ground.stampBurn(ev.x, ev.z, ev.cr * 0.9);
    glow.flash(ev.x, ev.z, r * 1.5, ORANGE, 1.1, 0.55);
    glow.flash(ev.x, ev.z, r * 0.9, 0xffffff, 1.4, 0.18);
    glow.flash(ev.x, ev.z, r * 0.7, HOT, 1, 0.3);
    if (FLASH.dim >= 1) fx.lights.flash(ev.x, gy + 1, ev.z, ORANGE, 80, 0.4, r * 4);
    for (let k = 0; k < 26; k++) { const a = Math.random() * 6.283, sp = 4 + Math.random() * 8; fx.particles.emitDir(ev.x, gy + 0.4, ev.z, Math.cos(a) * sp, 3 + Math.random() * 7, Math.sin(a) * sp, k % 3 ? EMBER : HOT, 0.22, 0.6 + Math.random() * 0.4, 2.6); }
    fx.shake = Math.min(1, fx.shake + (big ? 0.85 : 0.6));
  }

  /** per frame: the leap height, the comet shell, fire trail and cracks. hx/hz = the hero's drawn ground position. */
  update(dt: number, sim: Sim, cam: THREE.Camera, fx: Fx, hx: number, hz: number): void {
    const d = sim.dive;
    if (d.active && !this.airborne) { this.airborne = true; this.tl = d.t; this.dur = d.dur; this.x0 = d.x0; this.z0 = d.z0; this.x1 = d.x1; this.z1 = d.z1; this.evo = d.evo; }
    if (!d.active && this.airborne) { this.airborne = false; this.lift = 0; this.pitch = 0; this.meteor = false; }
    if (d.active) this.meteor = d.fx === 'roarfall';
    this.cometT.value += dt;
    const M = this.meteor, rise = M ? METEOR_FALL : RISE, H = DIVE_H * (M ? METEOR_H : this.evo ? 1.25 : 1);
    let u = 0;
    if (this.airborne) {
      this.tl = Math.min(this.dur, this.tl + dt);
      u = this.hold ?? this.tl / Math.max(1e-3, this.dur);
      this.lift = diveHeight(u, H, M);
      this.pitch = u > rise ? Math.min(0.55, (u - rise) / (1 - rise) * 0.8) : M ? -0.2 * Math.min(1, u / METEOR_RISE) : -0.15 * (u / RISE);
    }
    // comet shell + trail while crashing down
    const gy = this.ground.sample(hx, hz), y = gy + this.lift + 1.0;
    _v.set(hx, y, hz);
    const crash = this.airborne && u > rise * (M ? 0.98 : 0.85);
    if (this.airborne) {
      { // direction of travel along the drawn arc (the sim's ground path eases over 85% of the airtime; the height is diveHeight)
        const e = 1e-3, uu = Math.min(1 - e, Math.max(e, u));
        const g = (w: number) => { const v = Math.min(1, w / 0.85); return v * v * (3 - 2 * v); };
        const ds = (g(uu + e) - g(uu - e)) / (2 * e), dh = (diveHeight(uu + e, H, M) - diveHeight(uu - e, H, M)) / (2 * e);
        _x.set((this.x1 - this.x0) * ds, dh, (this.z1 - this.z0) * ds);
        if (_x.lengthSq() < 1e-6) _x.set(0, -1, 0);
      }
      if (this.hasPrev) {
        this.trailAcc += dt;
        if (this.trailAcc > 0.016) { this.trailAcc = 0; this.trail.copyWithin(3, 0, (TRAIL - 1) * 3); this.trail[0] = _v.x; this.trail[1] = _v.y; this.trail[2] = _v.z; this.trailN = Math.min(TRAIL, this.trailN + 1); }
      }
      this.prev.copy(_v); this.hasPrev = true;
      const k = crash ? Math.min(1, (u - rise * (M ? 0.98 : 0.85)) / (M ? 0.1 : 0.15)) : 0;
      this.cometK.value = k;
      this.comet.visible = k > 0.01;
      if (this.comet.visible) {
        // axial billboard along the motion; the head circle centred on his body, pushed a little away from the camera
        _x.normalize();
        _y.set(cam.position.x - _v.x, cam.position.y - _v.y, cam.position.z - _v.z).normalize();
        const head = (M ? 2.8 : this.evo ? 2.3 : 1.8) * (0.7 + 0.3 * k), A = 3.2, L = head * A;
        _z.crossVectors(_y, _x); if (_z.lengthSq() < 1e-6) _z.set(0, 0, 1); _z.normalize();
        _m.makeBasis(_a.copy(_x).multiplyScalar(L), _b.copy(_z).multiplyScalar(head), _y);
        const back = (0.5 - (A - 0.55) / A) * L;
        _m.setPosition(_v.x + _x.x * back - _y.x * 0.6, _v.y + _x.y * back - _y.y * 0.6, _v.z + _x.z * back - _y.z * 0.6);
        this.comet.matrixAutoUpdate = false; this.comet.matrix.copy(_m); this.comet.matrixWorldNeedsUpdate = true;
        for (let i = 0; i < 3; i++) fx.particles.emitDir(_v.x + (Math.random() - 0.5) * 0.8, _v.y + (Math.random() - 0.5) * 0.8, _v.z + (Math.random() - 0.5) * 0.8, -_x.x * 4 + (Math.random() - 0.5) * 2, -_x.y * 4 + Math.random() * 2, -_x.z * 4 + (Math.random() - 0.5) * 2, Math.random() < 0.4 ? HOT : Math.random() < 0.6 ? ORANGE : EMBER, 0.3, 0.35, 2.6);
      } else if (u < (M ? METEOR_RISE : RISE) && Math.random() < 0.7) fx.particles.emitDir(_v.x, _v.y - 0.8, _v.z, (Math.random() - 0.5), -2, (Math.random() - 0.5), 0xfff0d8, 0.18, 0.25, 2.2);
    } else { this.comet.visible = false; this.hasPrev = false; this.trailN = Math.max(0, this.trailN - 1); }
    // ribbons: the fire trail behind him, then the cracks
    const R = this.ribbons, q = R.pts, camPos = cam.position;
    R.begin();
    if (this.trailN > 2) {
      const N = Math.min(this.trailN, R.seg + 1), fade = crash ? 1 : M ? 0.8 : 0.6;
      for (let i = 0; i < N; i++) { const o = i * RS_STRIDE, s = i * 3, u2 = i / (N - 1); q[o] = this.trail[s]; q[o + 1] = this.trail[s + 1]; q[o + 2] = this.trail[s + 2]; q[o + 3] = (M ? 1.7 : this.evo ? 1.5 : 1.15) * (1 - u2) * fade; q[o + 4] = (1 - u2) * fade; }
      // meteor: a white-hot streak on the way up, the fire ribbon on the way down
      if (M && !crash) { _edge.setHex(0xdcefff).multiplyScalar(1.5); _core.setHex(0xffffff).multiplyScalar(1.8); }
      else { _edge.setHex(ORANGE).multiplyScalar(1.4); _core.setHex(HOT).multiplyScalar(1.6); }
      R.commit(N, camPos, _edge, _core, 0.45);
    }
    for (const c of this.cracks) {
      if (!c.on) continue;
      c.t += dt; if (c.t > c.life) { c.on = false; continue; }
      const k = c.t / c.life, a = Math.min(1, (1 - k) * 2.2), cool = Math.min(1, k * 1.6);
      for (let i = 0; i < c.n; i++) { const o = i * RS_STRIDE, s = i * 3, u2 = i / Math.max(1, c.n - 1); q[o] = c.p[s]; q[o + 1] = c.p[s + 1]; q[o + 2] = c.p[s + 2]; q[o + 3] = c.w * (1 - u2 * 0.8) * (0.6 + 0.4 * (1 - k)); q[o + 4] = a; }
      _edge.setHex(c.hot ? EMBER : 0x3a2414).lerp(_core.setHex(0x40140a), cool).multiplyScalar(c.hot ? 1.6 * (1 - cool) + 0.4 : 1);
      _core.setHex(HOT).lerp(_v2.setHex(ORANGE), cool).multiplyScalar(c.hot ? 1.8 * (1 - cool * 0.7) : 0.6);
      R.commit(c.n, camPos, _edge, _core, c.hot ? 0.45 : 0.2);
    }
    R.end();
  }

  /** ground light under the comet as it falls (bright when it is low) */
  glowPools(g: GroundGlow, hx: number, hz: number): void {
    if (!this.airborne) return;
    const gy = this.ground.sample(hx, hz);
    g.blob(hx, gy + this.lift + 1, hz, 2.2, ORANGE, this.cometK.value as number * 1.1 + 0.2);
  }

  clear(): void { this.airborne = false; this.lift = 0; this.pitch = 0; this.meteor = false; this.comet.visible = false; for (const c of this.cracks) c.on = false; this.ribbons.clear(); this.trailN = 0; }
}
const _v2 = new THREE.Color();
